import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { PasswordInput } from '../../components/PasswordInput';
import { ApiError } from '../../lib/api';
import { resetPassword } from './passwordReset';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') ?? '', [params]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('兩次輸入的密碼不一致。');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`無法重設密碼：${err.message}`);
      } else {
        setError('無法重設密碼：網路連線異常，請稍後再試。');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-12)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-5)', marginBottom: 'var(--spacing-4)' }}>
          <BrandLogo />
          <div>
            <h1 style={{ margin: 0, fontSize: 'var(--font-size-2xl)', letterSpacing: 'var(--letter-spacing-tight)' }}>重設密碼</h1>
            <p className="text-tertiary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              LS Cloud Admin Console
            </p>
          </div>
        </div>
        {done ? (
          <>
            <p className="text-secondary" style={{ marginTop: 0, fontSize: 'var(--font-size-md)' }}>
              密碼已更新，請使用新密碼登入。
            </p>
            <Link className="btn-primary" to="/login" style={{ width: '100%' }}>
              前往登入
            </Link>
          </>
        ) : (
          <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--spacing-6)' }}>
            <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
              <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                新密碼
              </span>
              <PasswordInput
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={error !== null}
              />
            </label>
            <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
              <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                確認新密碼
              </span>
              <PasswordInput
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={error !== null}
              />
            </label>
            {error && (
              <p className="error-text" role="alert" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? '更新中…' : '更新密碼'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
